import React, { useEffect, useMemo } from 'react';
import { HalfLayout } from './HalfLayout';
import ResponseTypo from './ResponseTypo';
import { StoryPlayer } from './StoryPlayer';
import { useSiteConfig } from '../../../hooks/useSiteConfig';
import { parseSiteConfigValue } from '../../../api/utils/api/siteConfigApi';
import {
  SITE_CONFIG_KEY,
  TeacherReviewItem,
} from '../../../constants/types/site-config-type';

// API 조회 실패 시 사용할 기본값
const REVIEW_TEXT_FALLBACK =
  '가정, 교회, 공부방\n내가 있는 곳 어디서든지 클래스를 오픈하여\n다음 세대를 양육할 수 있습니다.';

const REVIEW_FALLBACK: TeacherReviewItem[] = [
  {
    name: '이정은 선생님',
    hashtags: ['교회', '주중'],
    photoUrl: process.env.PUBLIC_URL + '/images/home/story/videoReview1.png',
    videoUrl: 'https://youtu.be/UD1P3GtEnVo?si=SQBGeoqKBSgYLzhb',
  },
  {
    name: '이수경 선생님',
    hashtags: ['자녀', '대안학교'],
    photoUrl: process.env.PUBLIC_URL + '/images/home/story/videoReview2.png',
    videoUrl: 'https://youtu.be/75jgHQDdwp8?si=xSfqgjm9cE0Wa7k5',
  },
  {
    name: '박리나 선생님',
    hashtags: ['공부방'],
    photoUrl: process.env.PUBLIC_URL + '/images/home/story/videoReview3.png',
    videoUrl: 'https://youtu.be/0AOmbOiGkVQ?si=neFBynfBwUuohQEM',
  },
  {
    name: '성소영 선생님',
    hashtags: ['자녀', '교습소'],
    photoUrl: process.env.PUBLIC_URL + '/images/home/story/videoReview4.png',
    videoUrl: 'https://youtu.be/YyKOUcq8BGQ?si=n6w8AsEIqcSlbWkY',
  },
  {
    name: '만남의교회',
    hashtags: ['교회', '주중'],
    photoUrl: process.env.PUBLIC_URL + '/images/home/story/videoReview5.png',
    videoUrl: 'https://youtu.be/GW2c9S7xYiA?si=LSnYO23W_0X1cpBD',
  },
];

const SITE_CONFIG_KEYS = [
  SITE_CONFIG_KEY.JEBS_STORY_TEACHER_REVIEW_TEXT,
  SITE_CONFIG_KEY.JEBS_STORY_TEACHER_REVIEW_LIST,
];

/** 젭스 스토리 - 교사 영상 후기 영역 */
export const StoryReview = () => {
  const [review, setReview] = React.useState(0);
  const [autoPlay, setAutoPlay] = React.useState(false);
  const { configs, loading } = useSiteConfig(SITE_CONFIG_KEYS);
  const text =
    configs[SITE_CONFIG_KEY.JEBS_STORY_TEACHER_REVIEW_TEXT] ||
    REVIEW_TEXT_FALLBACK;

  const reviews = useMemo(() => {
    const parsed = parseSiteConfigValue<TeacherReviewItem>(
      configs[SITE_CONFIG_KEY.JEBS_STORY_TEACHER_REVIEW_LIST],
    );
    return parsed.length ? parsed : REVIEW_FALLBACK;
  }, [configs]);

  // 목록이 바뀌어 인덱스가 범위를 넘을 때만 처음으로 되돌린다
  useEffect(() => {
    setReview((prev) => (prev < reviews.length ? prev : 0));
  }, [reviews]);

  // 리뷰 변경시 자동재생 & 영상 종료시 다음 영상 재생
  useEffect(() => {
    setAutoPlay(review !== 0);
  }, [review]);
  return (
    <section className="bg-black/[0.05] p-5 lg:pb-14 lg:pt-14">
      <div className="lg:m-auto lg:max-w-[1024px]">
        <HalfLayout
          left={
            <div className="flex flex-col items-start text-left">
              <img src="./svg/story/icon-book.svg" alt="icon book" />
              <ResponseTypo variant_sm="h5" variant_lg="h4" className="mt-3 whitespace-pre-line break-keep lg:text-xl xl:text-2xl">
                {text}
              </ResponseTypo>
            </div>
          }
          right={
            <StoryPlayer
              controls
              onEnded={() => setReview((review + 1) % reviews.length)}
              playing={autoPlay}
              // 폴백 URL로 먼저 로드하지 않도록 설정 조회 후 넘긴다
              url={loading ? undefined : reviews[review]?.videoUrl}
            />
          }
        />
      </div>
      <div className="mt-8 grid grid-cols-2 gap-5 lg:mx-auto lg:flex lg:max-w-[1024px]">
        {reviews.map((item, i) => (
          <div
            // 설정 조회 후 img 노드를 재사용해 썸네일이 빈칸으로 깜빡이지 않게 한다
            key={i}
            // 리뷰 개수(3~8개)가 바뀌어도 한 줄을 균등 분할한다
            className={`flex justify-center lg:flex-1`}
            onClick={() => setReview(i)}
          >
            <div className="group flex w-fit cursor-pointer flex-col items-center">
              <div className="relative h-16 w-16">
                {review === i && (
                  <div className="absolute right-0 top-0 h-2 w-2 animate-pulse rounded-full bg-red5"></div>
                )}
                <img
                  src={item.photoUrl}
                  alt={item.name}
                  // 어드민 업로드 이미지는 비율이 제각각이라 원형 크기를 고정한다
                  className={`absolute left-0 top-0 h-16 w-16 rounded-full border-purple5 object-cover transition-transform duration-100 ease-in-out group-hover:scale-110`}
                />
              </div>
              <div className="mt-2 w-full text-center text-lg">
                {item.hashtags?.map((tag) => `#${tag}`).join(' ')}
              </div>
              <div className="w-full text-center text-sm text-gray9">
                {item.name}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
